import React from "react";

/**
 * Renders lucide-react components, image icons, or SVG icons with fallback.
 * @param {React.ElementType|string} icon - Lucide component or image name
 * @param {'component'|'image'|'svg'|'text'} iconType - Type of icon
 * @param {string} className - Additional CSS classes
 * @param {'sm'|'md'|'lg'|'xl'} size - Icon size
 * @param {boolean} isWhite - Apply white filter to images
 */
const IconRenderer = ({
  icon: IconComponent,
  iconType = "component",
  alt = "",
  className = "",
  fallback = null,
  size = "md",
  isWhite = false,
  ...props
}) => {
  const sizeMap = { sm: 14, md: 18, lg: 22, xl: 28 };
  const sizeClasses = { sm: "w-4 h-4", md: "w-5 h-5", lg: "w-6 h-6", xl: "w-8 h-8" };
  const pixelSize = sizeMap[size] || 18;

  // If icon is a React component (lucide-react), render it directly
  if (typeof IconComponent === "function" || typeof IconComponent === "object") {
    return (
      <IconComponent
        size={pixelSize}
        className={`flex-shrink-0 ${className}`}
        strokeWidth={1.75}
        {...props}
      />
    );
  }

  // Image-based icons (cloud provider logos)
  if (iconType === "image") {
    return (
      <img
        src={`/${IconComponent}.png`}
        alt={alt || IconComponent}
        className={`${sizeClasses[size]} object-contain flex-shrink-0 ${
          isWhite ? "brightness-0 invert" : ""
        } ${className}`}
        onError={(e) => {
          e.target.style.display = "none";
        }}
        {...props}
      />
    );
  }

  // Fallback: render as text
  return (
    <span
      className={`${sizeClasses[size]} inline-flex items-center justify-center flex-shrink-0 ${className}`}
      {...props}
    >
      {IconComponent}
    </span>
  );
};

export default IconRenderer;
